/**
 * Exposure Hook & Context
 * Manages user exposure data and wallet balance
 * Extracted and organized from ibitplay codebase
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { API_URL } from '../utils/constants';

const ExposureContext = createContext();

const readUid = () => {
  const m = document.cookie.match(/(?:^|; )uid=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  const logged = localStorage.getItem('logged');
  return token && logged === 'true';
};

export const ExposureProvider = ({ children }) => {
  const [exposures, setExposures] = useState({});
  const [balance, setBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshAll = useCallback(async (uuid) => {
    if (!uuid) return;
    if (!isAuthenticated()) {
      setError('Authentication required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const eRes = await fetch(`${API_URL}/sportsbetting/exposures/${uuid}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (eRes.status === 401) {
        setError('Authentication required');
        return;
      }

      const eJ = await eRes.json();
      if (eJ.success) {
        setExposures(eJ.exposures);
      }

      const wRes = await fetch(`${API_URL}/sportsbetting/wallet/${uuid}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (wRes.status === 401) {
        setError('Authentication required');
        return;
      }

      const wJ = await wRes.json();
      if (wJ.success) {
        const newBal = +wJ.balance;
        setBalance(newBal);
        localStorage.setItem('credit', newBal.toString());
        window.dispatchEvent(new CustomEvent('credit_update', { detail: newBal }));
      }
    } catch (err) {
      console.error('❌ ExposureProvider.refreshAll error:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const uid = readUid();
      if (uid && isAuthenticated()) {
        const stored = Number(localStorage.getItem('credit') || 0);
        setBalance(stored);
        refreshAll(uid);
      } else {
        const stored = Number(localStorage.getItem('credit') || 0);
        setBalance(stored);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [refreshAll]);

  const patchMatchExposure = (matchId, newTeams) => {
    setExposures((prev) => ({
      ...prev,
      [matchId]: {
        match_title: prev[matchId]?.match_title || '',
        teams: {
          ...(prev[matchId]?.teams || {}),
          ...newTeams,
        },
      },
    }));
  };

  const patchBalance = (delta) => {
    setBalance((prev) => {
      const updated = prev + delta;
      localStorage.setItem('credit', updated.toString());
      window.dispatchEvent(new CustomEvent('credit_update', { detail: updated }));
      return updated;
    });
  };

  const getMatchExposure = (matchId) => {
    const match = exposures[matchId];
    if (!match) return 0;
    const losses = Object.values(match.teams || {})
      .filter((v) => v < 0)
      .map((v) => Math.abs(v));
    return losses.length ? Math.max(...losses) : 0;
  };

  const totalExposure = Object.values(exposures)
    .reduce((sum, m) => {
      const losses = Object.values(m.teams || {})
        .filter((v) => v < 0)
        .map((v) => Math.abs(v));
      const maxLoss = losses.length ? Math.max(...losses) : 0;
      return sum + maxLoss;
    }, 0)
    .toFixed(2);

  return (
    <ExposureContext.Provider
      value={{
        exposures,
        totalExposure,
        balance,
        patchMatchExposure,
        patchBalance,
        refreshAll,
        isLoading,
        error,
        isAuthenticated: isAuthenticated(),
        getMatchExposure,
      }}
    >
      {children}
    </ExposureContext.Provider>
  );
};

export const useExposure = () => {
  const context = useContext(ExposureContext);
  if (!context) throw new Error('useExposure must be used within ExposureProvider');
  return context;
};
